/**
 * Booking-summary and payment currency for one selected Hubwayz offer.
 * Supplier amount and supplier currency stay paired. A previous search
 * currency, a display-converted amount, or the cheapest room is not used.
 */

import { formatFx } from "../../packageProfileMockup/mockPackageProfileData";
import { buildUnifiedFlightHotelPaymentVm } from "../buildUnifiedFlightHotelPaymentVm";
import {
  hubwayzSettledChargeIls,
  readHubwayzSupplierMoney,
  resolveHubwayzHotelIlsPricing,
} from "./resolveHubwayzHotelIlsPricing";

function upper(code) {
  const c = String(code || "").trim().toUpperCase();
  return c || "";
}

/**
 * Cheapest offer whose supplier currency is the currency of this search.
 * Falls back to the cheapest room when none match.
 */
export function preferredHubwayzRoomId(rooms, displayCurrency) {
  const list = Array.isArray(rooms) ? rooms : [];
  const code = upper(displayCurrency);
  if (code) {
    const match = list.find(
      (room) => upper(room?.supplierCurrency || room?.currency) === code
    );
    if (match?.id != null) return match.id;
  }
  return list[0]?.id;
}

/**
 * Original supplier money of the offer the user selected.
 * Reads that offer's netPrice from the search payload when it is still here.
 */
export function readHubwayzSelectedOfferMoney(room, hubwayzResults) {
  const offerCode = String(room?.hubwayzOfferCode || room?.id || "").trim();
  const results = Array.isArray(hubwayzResults) ? hubwayzResults : [];
  const raw = offerCode
    ? results.find((offer) => String(offer?.code || "").trim() === offerCode)
    : null;
  if (raw) {
    const money = readHubwayzSupplierMoney(raw);
    if (money.amount && money.currency) {
      return {
        supplierAmount: money.amount,
        supplierCurrency: money.currency,
        offerCode,
      };
    }
  }
  const supplierAmount = Number(room?.supplierAmount ?? room?.hotelNetTotal);
  const supplierCurrency = upper(room?.supplierCurrency || room?.currency);
  if (!Number.isFinite(supplierAmount) || supplierAmount <= 0 || !supplierCurrency) {
    return null;
  }
  return { supplierAmount, supplierCurrency, offerCode: offerCode || null };
}

export function priceHubwayzSelectedOffer({
  room,
  hubwayzResults = null,
  currencyRate = null,
  currencyRateSource = null,
  getHrCommissionPercent,
  getHrCommissionPercentHotels,
  hrCommissionHotelsSetting,
  getAgentCommissionPercent,
  getCreditCardFeePercent,
  agentCommission = null,
} = {}) {
  void currencyRate;
  void currencyRateSource;
  void getHrCommissionPercent;
  void getHrCommissionPercentHotels;
  void hrCommissionHotelsSetting;
  void getAgentCommissionPercent;
  void getCreditCardFeePercent;
  void agentCommission;
  const money = readHubwayzSelectedOfferMoney(room, hubwayzResults);
  const charge = Number(
    room?.chargeIls ??
      room?.verifiedQuote?.chargeIls ??
      room?.hubwayzVerifiedQuote?.chargeIls ??
      room?.agentNetIls
  );
  if (!Number.isFinite(charge) || charge <= 0) {
    return {
      ok: false,
      reason: "server_quote_required",
      chargeIls: null,
      agentNetIls: null,
      supplierAmount: money?.supplierAmount ?? null,
      supplierCurrency: money?.supplierCurrency ?? null,
      offerCode: money?.offerCode ?? null,
    };
  }
  const customerSale = Number(
    room?.customerSaleIls ??
      room?.verifiedQuote?.customerSaleIls ??
      room?.hubwayzVerifiedQuote?.customerSaleIls
  );
  return {
    ok: true,
    reason: null,
    chargeIls: charge,
    agentNetIls: charge,
    customerSaleIls: Number.isFinite(customerSale) && customerSale > 0 ? customerSale : charge,
    supplierAmount: money?.supplierAmount ?? null,
    supplierCurrency: money?.supplierCurrency ?? null,
    offerCode: money?.offerCode ?? null,
    fxRate: null,
    fxApplied: false,
  };
}

/**
 * Pre-book may confirm the same supplier currency. A different currency is a
 * previous offer and is not adopted.
 */
export function adoptHubwayzPrebookMoney(selected, prebookDisplay) {
  const prebook = prebookDisplay
    ? readHubwayzSupplierMoney(prebookDisplay)
    : { amount: null, currency: null };
  const prebookAmount = Number(prebook.amount);
  const prebookCurrency = upper(prebook.currency);
  const prebookOk =
    Number.isFinite(prebookAmount) && prebookAmount > 0 && !!prebookCurrency;
  const selectedCurrency = upper(selected?.supplierCurrency);
  const selectedAmount = Number(selected?.supplierAmount);
  const selectedOk =
    !!selectedCurrency && Number.isFinite(selectedAmount) && selectedAmount > 0;

  const priceChanged =
    prebookOk &&
    selectedOk &&
    prebookCurrency === selectedCurrency &&
    Math.abs(prebookAmount - selectedAmount) >= 0.02;
  if (prebookOk && selectedOk && prebookCurrency !== selectedCurrency) {
    return {
      supplierAmount: selectedAmount,
      supplierCurrency: selectedCurrency,
      source: "selected_offer",
      mismatch: true,
      priceChanged: false,
      previousSupplierAmount: null,
    };
  }
  if (prebookOk && (!selectedOk || prebookCurrency === selectedCurrency)) {
    return {
      supplierAmount: prebookAmount,
      supplierCurrency: prebookCurrency,
      source: "prebook",
      mismatch: false,
      priceChanged,
      previousSupplierAmount: priceChanged ? selectedAmount : null,
    };
  }
  if (selectedOk) {
    return {
      supplierAmount: selectedAmount,
      supplierCurrency: selectedCurrency,
      source: "selected_offer",
      mismatch: false,
      priceChanged: false,
      previousSupplierAmount: null,
    };
  }
  return null;
}

/**
 * Drop stored supplier money that belongs to a different offer.
 * Returns the selected offer's amount and currency together.
 */
export function reconcileHubwayzDraftToSelectedOffer(draft, offerMoney) {
  const base = draft && typeof draft === "object" ? { ...draft } : {};
  if (!offerMoney?.supplierCurrency || !(Number(offerMoney.supplierAmount) > 0)) {
    return { draft: base, cleared: false };
  }
  const storedCurrency = upper(base.supplierCurrency || base.currency);
  const storedAmount = Number(base.supplierAmount);
  const storedCode = String(base.hubwayzOfferCode || "").trim();
  const sameCode =
    !offerMoney.offerCode || !storedCode || storedCode === offerMoney.offerCode;
  const sameCurrency = storedCurrency === offerMoney.supplierCurrency;
  const sameAmount =
    !Number.isFinite(storedAmount) ||
    Math.abs(storedAmount - Number(offerMoney.supplierAmount)) < 0.02;
  if (sameCode && sameCurrency && sameAmount && upper(base.supplierCurrency) === offerMoney.supplierCurrency) {
    return { draft: base, cleared: false };
  }
  delete base.fxRate;
  delete base.ilsSalePrice;
  delete base.chargeIls;
  delete base.hubwayzIlsPricing;
  delete base.hotelTotalFx;
  delete base.settledChargeIls;
  delete base.perFx;
  base.currency = offerMoney.supplierCurrency;
  base.supplierCurrency = offerMoney.supplierCurrency;
  base.supplierAmount = Number(offerMoney.supplierAmount);
  base.totalFx = Number(offerMoney.supplierAmount);
  if (offerMoney.offerCode) base.hubwayzOfferCode = offerMoney.offerCode;
  return { draft: base, cleared: true };
}

/**
 * Hotels payment preview. Charges supplierAmount in supplierCurrency.
 * Ignores draft.currency when it disagrees, and does not use totalFx as the base.
 */
export function buildHubwayzHotelsPaymentPreview(
  bookingDraft,
  appliedSearch = null,
  currencyRate = null,
  feeCtx = {}
) {
  const supplierAmount = Number(bookingDraft?.supplierAmount);
  const supplierCurrency = upper(bookingDraft?.supplierCurrency);
  const staleLabel = upper(bookingDraft?.currency);
  const paired = Number.isFinite(supplierAmount) && supplierAmount > 0 && !!supplierCurrency;
  if (!paired) {
    const vm = buildUnifiedFlightHotelPaymentVm(
      bookingDraft,
      appliedSearch,
      currencyRate
    );
    return { ...vm, customerPrice: null, ignoredStaleCurrency: false };
  }
  const quote = bookingDraft?.hubwayzVerifiedQuote || bookingDraft?.verifiedQuote;
  const serverCharge = Number(
    quote?.chargeIls ?? bookingDraft?.chargeIls ?? bookingDraft?.settledChargeIls
  );
  const customerPrice =
    Number.isFinite(serverCharge) && serverCharge > 0
      ? {
          ok: true,
          reason: null,
          chargeIls: serverCharge,
          agentNetIls: serverCharge,
          supplierAmount,
          supplierCurrency,
        }
      : resolveHubwayzHotelIlsPricing();
  const settled = hubwayzSettledChargeIls(customerPrice);
  const vm = buildUnifiedFlightHotelPaymentVm(
    settled != null
      ? { ...bookingDraft, settledChargeIls: settled, currency: supplierCurrency }
      : { ...bookingDraft, totalFx: null, hotelTotalFx: null, currency: supplierCurrency },
    appliedSearch,
    currencyRate
  );
  if (!customerPrice.ok) {
    vm.financial = {
      ...vm.financial,
      totalAmount: null,
      remainingBalance: null,
    };
  }
  return {
    ...vm,
    customerPrice,
    ignoredStaleCurrency: Boolean(staleLabel && staleLabel !== supplierCurrency),
  };
}

export function formatHubwayzSupplierOriginalLabel(amount, currency) {
  const code = upper(currency);
  const n = Number(amount);
  if (!code || code === "ILS" || !Number.isFinite(n)) return "";
  if (code === "USD" || code === "EUR") return formatFx(n, code);
  return `${Math.round(n).toLocaleString("en-US")} ${code}`;
}

export function formatHubwayzSupplierRateLabel(currency, rate) {
  const code = upper(currency);
  const n = Number(rate);
  if (!code || code === "ILS" || !Number.isFinite(n) || n <= 0) return "";
  return `1 ${code} = ₪${n.toLocaleString("he-IL", { maximumFractionDigits: 4 })}`;
}
